import { useState, useEffect, useCallback } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { env, LANDING_URL } from "../../env";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANGUAGE_STORAGE_KEY } from "../../i18n";
import LanguageToggle from "../../components/Common/LanguageToggle";
import { Controller, useForm } from "react-hook-form";
import { ArrowRight, Plus, ChevronDown } from "lucide-react";
import Select, { components } from "react-select";
import { defaultReactSelectStyles } from "../../utils/reactSelectStyles";
import LocationAutocomplete from "../../components/Common/LocationAutocomplete";
import AuthLayout from "../../components/Auth/AuthLayout";
import LearnMoreModal from "../../components/Auth/LearnMoreModal";
import SocialModal from "../../components/Auth/SocialModal";
import Button from "../../components/Common/Button";
import ErrorMessage from "../../components/Common/ErrorMessage";
import CustomCheckbox from "../../components/Common/CustomCheckbox";
import { register as registerRequest, registerKlaviyoProfile, verifyTurnstile } from "../../api/authApi";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { showFirstFormErrorToast } from "../../utils/formErrorToast";
import { showErrorToast } from "../../utils/toast";
import { validateFormat } from "../../utils/socialValidation";
import { consumeSocialVerificationResult } from "../../utils/socialVerificationSession";
import { getSocialPlatformList } from "../../api/postgre/socialPlatformApi";
import { getRegisterCategories, getRegisterAgeList, getCountryList } from "../../api/postgre/registerApi";

const registerSelectStyles = {
    ...defaultReactSelectStyles,
    control: (base, state) => ({
        ...defaultReactSelectStyles.control(base),
        borderRadius: "0.75rem",
        backgroundColor: "#f7f7f7",
        borderColor: state.isFocused ? "#0f1b8f" : "#E2E8F0",
        boxShadow: "none",
        outline: "none",
        minHeight: "48px",
        "&:hover": {
            borderColor: state.isFocused ? "#0f1b8f" : "#E2E8F0",
            backgroundColor: "#f7f7f7",
        },
        "&:active": {
            backgroundColor: "#f7f7f7",
        },
    }),
};

const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
        <ChevronDown className="h-4 w-4 text-slate-400" />
    </components.DropdownIndicator>
);

const CheckboxOption = (props) => (
    <components.Option {...props}>
        <div className={`flex items-center gap-2 ${props.isDisabled ? "opacity-40" : ""}`}>
            <input
                type="checkbox"
                checked={props.isSelected}
                onChange={() => null}
                disabled={props.isDisabled}
                className="accent-primary h-4 w-4 rounded"
            />
            <span className={`text-sm ${props.isDisabled ? "text-slate-400 line-through" : "text-slate-700"}`}>
                {props.label}
            </span>
        </div>
    </components.Option>
);


const REGISTER_DRAFT_KEY = "register_form_draft";

const saveRegisterDraft = (values, handles, socialLinks, verifiedPlatforms) => {
    try {
        sessionStorage.setItem(REGISTER_DRAFT_KEY, JSON.stringify({ values, handles, socialLinks, verifiedPlatforms }));
    } catch {}
};

const loadRegisterDraft = () => {
    try {
        const raw = sessionStorage.getItem(REGISTER_DRAFT_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const clearRegisterDraft = () => sessionStorage.removeItem(REGISTER_DRAFT_KEY);

// Drafts saved before multi-account-per-platform shipped stored a single
// string/boolean per platform key instead of an array — normalize old-shape
// drafts so a mid-flight sessionStorage draft doesn't crash the new code.
const toEntryArrayMap = (map) =>
    Object.fromEntries(
        Object.entries(map || {}).map(([key, value]) => [
            key,
            Array.isArray(value) ? value : (value ? [value] : []),
        ])
    );

// Given the handle/index a verification round-trip reports back, resolve
// which array slot it actually belongs to — trusting the index when it's
// still in range, otherwise falling back to matching by handle text (older
// verification links didn't carry an index at all).
const resolveEntryIndex = (arr, handle, index) => {
    if (index != null && index >= 0 && index < arr.length) return index;
    const matched = arr.lastIndexOf(handle);
    if (matched >= 0) return matched;
    return Math.max(arr.length - 1, 0);
};

const normalizePhone = (phone) => {
    const stripped = String(phone || "").replace(/\s+/g, "");
    if (!stripped) return "";
    if (stripped.startsWith("00")) return `+${stripped.slice(2)}`;
    if (stripped.startsWith("+")) return stripped;
    return `+30${stripped}`;
};

const normalizeName = (value) => String(value || "").trim();

const REGISTER_DEFAULT_VALUES = {
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    location: "",
    phone: "",
    age: null,
    categories: [],
    acceptedTerms: false,
    acceptedAge: false,
    acceptedMarketing: false,
    acceptedSms: false,
};

const Register = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showSocialModal, setShowSocialModal] = useState(false);
    const [showLearnModal, setShowLearnModal] = useState(false);
    const [socialError, setSocialError] = useState("");
    const [platforms, setPlatforms] = useState([]);
    const [handles, setHandles] = useState({});
    const [socialLinks, setSocialLinks] = useState({});
    const [verifiedPlatforms, setVerifiedPlatforms] = useState({});
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [ageOptions, setAgeOptions] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);
    const { run, isLoading, clearError } = useAsyncAction();
    const [turnstileToken, setTurnstileToken] = useState(null);
    const [turnstileVerified, setTurnstileVerified] = useState(false);
    const [submitError, setSubmitError] = useState("");
    // Controlled via env.js so registration can be opened/closed without a
    // code change + redeploy — regenerate env.js with
    // --registrationEnabled=true|false (see scripts/encrypt-env.mjs).
    const isRegistrationEnabled = env.REACT_APP_REGISTRATION_ENABLED === "true";

    const {
        control,
        handleSubmit,
        reset,
        getValues,
        formState: { errors, isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: REGISTER_DEFAULT_VALUES,
    });

    useEffect(() => {
        getRegisterAgeList()
            .then((list) => setAgeOptions(list))
            .catch(() => {});
        const lang = localStorage.getItem(LANGUAGE_STORAGE_KEY) || "en";
        getRegisterCategories(lang)
            .then((list) => setCategoryOptions(list))
            .catch(() => {});
        getCountryList()
            .then((list) => setCountryOptions(list))
            .catch(() => {});
    }, []);

    useEffect(() => {
        getSocialPlatformList()
            .then((list) => {
                setPlatforms(list);
                // Seed defaults for any platform not already set, but never
                // clobber values already restored from the register draft
                // (this fetch resolves after that sync restore, so a plain
                // overwrite here would wipe out a just-completed verification).
                setHandles((prev) => ({ ...Object.fromEntries(list.map((p) => [p.key, []])), ...prev }));
                setSocialLinks((prev) => ({ ...Object.fromEntries(list.map((p) => [p.key, []])), ...prev }));
                setVerifiedPlatforms((prev) => ({ ...Object.fromEntries(list.map((p) => [p.key, []])), ...prev }));
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        const draft = loadRegisterDraft();
        if (draft?.values) reset({ ...REGISTER_DEFAULT_VALUES, ...draft.values });
        if (draft?.handles) setHandles(toEntryArrayMap(draft.handles));
        if (draft?.socialLinks) setSocialLinks(toEntryArrayMap(draft.socialLinks));
        if (draft?.verifiedPlatforms) setVerifiedPlatforms(toEntryArrayMap(draft.verifiedPlatforms));

        const result = consumeSocialVerificationResult();
        if (!result) return;
        const { platform, handle, status, message, index } = result;
        // Reopen the modal so the platform's updated status (Verified, or the
        // error) shows in place, instead of landing on a closed modal that
        // looks like the verification attempt was lost.
        setShowSocialModal(true);
        if (status === "verified") {
            // Only overwrite the stored handle with the backend's confirmed
            // one on an actual success — on a mismatch/error, the backend may
            // still return a `handle` (the real authenticated account), and
            // writing that over what the user typed looks like their input
            // silently got replaced with someone else's username.
            if (platform && handle) {
                setHandles((prev) => {
                    const arr = [...(prev[platform] || [])];
                    const targetIndex = resolveEntryIndex(arr, handle, index);
                    arr[targetIndex] = handle;
                    return { ...prev, [platform]: arr };
                });
            }
            setVerifiedPlatforms((prev) => {
                const arr = [...(prev[platform] || [])];
                const targetIndex = resolveEntryIndex(arr, handle, index);
                arr[targetIndex] = true;
                return { ...prev, [platform]: arr };
            });
            setSocialError("");
            return;
        }
        if (platform) {
            setVerifiedPlatforms((prev) => {
                const arr = [...(prev[platform] || [])];
                const targetIndex = resolveEntryIndex(arr, handle, index);
                arr[targetIndex] = false;
                return { ...prev, [platform]: arr };
            });
        }
        if (message) {
            setSocialError(message);
        }
    }, [reset]);

    useEffect(() => {
        if (!submitError) return;
        const timer = setTimeout(() => setSubmitError(""), 8000);
        return () => clearTimeout(timer);
    }, [submitError]);

    const isSocialAdded = platforms.some((p) => (handles[p.key] || []).some((h) => (h || "").trim()));
    const hasUnverifiedSocial = platforms.some((p) =>
        (handles[p.key] || []).some((h, i) => (h || "").trim() && !(verifiedPlatforms[p.key] || [])[i])
    );
    const canSubmit = isRegistrationEnabled && isValid && !isLoading && isSocialAdded && !hasUnverifiedSocial && turnstileVerified;
    // index === null means "append a new account for this platform";
    // otherwise it replaces the existing entry at that index.
    const handleChange = (key, index, value) => {
        setHandles((prev) => {
            const arr = [...(prev[key] || [])];
            const targetIndex = index == null ? arr.length : index;
            const previousValue = (arr[targetIndex] || "").trim();
            const nextValue = (value || "").trim();
            arr[targetIndex] = value;
            if (previousValue !== nextValue) {
                setVerifiedPlatforms((verifiedPrev) => {
                    const verifiedArr = [...(verifiedPrev[key] || [])];
                    verifiedArr[targetIndex] = false;
                    return { ...verifiedPrev, [key]: verifiedArr };
                });
            }
            return { ...prev, [key]: arr };
        });
        if (socialError) setSocialError("");
    };

    const handleLinkChange = (key, index, value) => {
        setSocialLinks((prev) => {
            const arr = [...(prev[key] || [])];
            const targetIndex = index == null ? arr.length : index;
            arr[targetIndex] = value;
            return { ...prev, [key]: arr };
        });
        if (socialError) setSocialError("");
    };

    const handleRemoveEntry = (key, index) => {
        setHandles((prev) => {
            const arr = [...(prev[key] || [])];
            arr.splice(index, 1);
            return { ...prev, [key]: arr };
        });
        setSocialLinks((prev) => {
            const arr = [...(prev[key] || [])];
            arr.splice(index, 1);
            return { ...prev, [key]: arr };
        });
        setVerifiedPlatforms((prev) => {
            const arr = [...(prev[key] || [])];
            arr.splice(index, 1);
            return { ...prev, [key]: arr };
        });
    };

    const handleBeforeVerify = useCallback(() => {
        saveRegisterDraft(getValues(), handles, socialLinks, verifiedPlatforms);
    }, [getValues, handles, socialLinks, verifiedPlatforms]);


    const onSubmit = async (values) => {
        clearError();
        setSocialError("");
        setSubmitError("");
        const firstName = normalizeName(values.firstName);
        const lastName = normalizeName(values.lastName);

        const payload = {
            FirstName: firstName,
            LastName: lastName,
            Email: values.email,
            CountryId: values.country ? Number(values.country) : null,
            Location: values.location || "",
            PhoneNumber: normalizePhone(values.phone),
            AccountAgeId: values.age?.value ? Number(values.age.value) : null,
            Category: (() => {
                const cats = values.categories || [];
                if (cats.some((c) => c.value === "all")) return "All";
                const ids = cats.map((c) => Number(c.value)).filter(Boolean);
                return ids.length ? ids.join(",") : "";
            })(),
            AcceptedTerms: values.acceptedTerms,
            IsAboveAge: values.acceptedAge,
            AcceptedMarketing: values.acceptedMarketing,
            IsConcernForNotification: values.acceptedSms,
            turnstile_token: turnstileToken,
            SocialMediaDetails: platforms.flatMap((p) => {
                const entryHandles = handles[p.key] || [];
                const entryLinks = socialLinks[p.key] || [];
                return entryHandles
                    .map((handle, index) => ({
                        handle: (handle || "").trim(),
                        link: (entryLinks[index] || "").trim(),
                    }))
                    .filter((entry) => entry.handle && validateFormat(p.key, entry.handle))
                    .map((entry) => ({
                        SocialMediaName: p.name,
                        SocialMediaCode: p.code,
                        SocialMediaAccountName: entry.handle,
                        SocialMediaUrl: entry.link,
                    }));
            }),
        };

        const result = await run( // eslint-disable-line no-unreachable
            () => registerRequest(payload),
            { errorFallback: t("AUTH_REGISTER_ERROR_FAILED") }
        );

        if (!result) return;

        if (result?.success === false || Number(result?.success) === 0) {
            const message = result?.message || result?.error || t("AUTH_REGISTER_ERROR_FAILED");
            showErrorToast(message);
            setSubmitError(message);
            return;
        }

        if (result?.success) {
            if (values.acceptedMarketing) {
                const countryName = countryOptions.find((o) => o.value === values.country)?.label || "";
                registerKlaviyoProfile({
                    Email: values.email,
                    FirstName: firstName,
                    LastName: lastName,
                    Phone: values.phone,
                    Country: countryName,
                    Location: values.location || "",
                    AcceptedMarketing: values.acceptedMarketing,
                }).catch(() => {});
            }
            clearRegisterDraft();
            navigate("/register-success");
        }
    };

    const handleFormInvalid = (formErrors) => {
        showFirstFormErrorToast(formErrors);
    };

    return (
        <>
            <div className="fixed top-6 right-6 z-50">
                <LanguageToggle />
            </div>
            <AuthLayout
                left={
                    <div className="relative h-96 w-full overflow-hidden bg-cream lg:h-auto">
                        <div className="absolute inset-0">
                            <img
                                src="/assets/images/common/korres-image.png"
                                alt=""
                                className="h-full w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-cream/40" />
                        </div>
                    </div>
                }
                right={
                    <div className="flex flex-col justify-center px-6 py-10 items-center sm:px-10 w-full">
                        <div>
                            <div className="flex items-center gap-3 text-sm font-semibold tracking-[0.2em] text-slate-700">
                                <a href={LANDING_URL}>
                                    <img
                                        src="/assets/icons/korres-shares-logo.png"
                                        alt={t("AUTH_REGISTER_ALT_KORRES_SHARES")}
                                        className="h-[4.5rem] w-[4.5rem] hover:opacity-75 transition-opacity"
                                    />
                                </a>
                            </div>

                            <div className="mt-4 max-w-lg">
                                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                                    {t("AUTH_TAGLINE")}
                                </p>
                                <p className="mt-3 text-sm text-slate-500">
                                    {t("AUTH_REGISTER_SUBTITLE")}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setShowLearnModal(true)}
                                    className="mt-3 text-xs font-semibold text-primary underline underline-offset-4 hover:opacity-75 focus:outline-none"
                                >
                                    {t("AUTH_REGISTER_LEARN_MORE")}
                                </button>
                            </div>

                            <form
                                className="mt-4 grid max-w-lg grid-cols-1 gap-4 sm:grid-cols-2"
                                onSubmit={handleSubmit(onSubmit, handleFormInvalid)}
                            >
                                <div>
                                    <Controller
                                        name="firstName"
                                        control={control}
                                        rules={{
                                            required: t("AUTH_VALIDATION_FIRST_NAME_REQUIRED"),
                                            validate: (value) => normalizeName(value) ? true : t("AUTH_VALIDATION_FIRST_NAME_REQUIRED"),
                                        }}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                onBlur={(e) => {
                                                    field.onBlur();
                                                    field.onChange(normalizeName(e.target.value));
                                                }}
                                                type="text"
                                                placeholder={t("AUTH_PLACEHOLDER_FIRST_NAME")}
                                                className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-700 outline-none focus:outline-none transition focus:border-primary"
                                            />
                                        )}
                                    />
                                    <ErrorMessage message={errors.firstName?.message} />
                                </div>
                                <div>
                                    <Controller
                                        name="lastName"
                                        control={control}
                                        rules={{
                                            required: t("AUTH_VALIDATION_LAST_NAME_REQUIRED"),
                                            validate: (value) => normalizeName(value) ? true : t("AUTH_VALIDATION_LAST_NAME_REQUIRED"),
                                        }}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                onBlur={(e) => {
                                                    field.onBlur();
                                                    field.onChange(normalizeName(e.target.value));
                                                }}
                                                type="text"
                                                placeholder={t("AUTH_PLACEHOLDER_LAST_NAME")}
                                                className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-700 outline-none focus:outline-none transition focus:border-primary"
                                            />
                                        )}
                                    />
                                    <ErrorMessage message={errors.lastName?.message} />
                                </div>
                                <div className="sm:col-span-2">
                                    <Controller
                                        name="email"
                                        control={control}
                                        rules={{
                                            required: t("AUTH_VALIDATION_EMAIL_REQUIRED"),
                                            pattern: {
                                                value: /\S+@\S+\.\S+/,
                                                message: t("AUTH_VALIDATION_EMAIL_INVALID"),
                                            },
                                        }}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                onKeyDown={(e) => e.key === " " && e.preventDefault()}
                                                onChange={(e) => field.onChange(e.target.value.replace(/\s/g, ""))}
                                                onBlur={(e) => { field.onBlur(); field.onChange(e.target.value.replace(/\s/g, "")); }}
                                                type="email"
                                                placeholder={t("AUTH_PLACEHOLDER_EMAIL")}
                                                className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-700 outline-none focus:outline-none transition focus:border-primary"
                                            />
                                        )}
                                    />
                                    <ErrorMessage message={errors.email?.message} />
                                </div>
                                <div className="sm:col-span-2">
                                    <Controller
                                        name="country"
                                        control={control}
                                        rules={{ required: t("AUTH_VALIDATION_COUNTRY_REQUIRED") }}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                value={countryOptions.find((option) => option.value === field.value) || null}
                                                options={countryOptions}
                                                placeholder={t("AUTH_REGISTER_COUNTRY_PLACEHOLDER")}
                                                classNamePrefix="react-select"
                                                components={{ DropdownIndicator }}
                                                styles={registerSelectStyles}
                                                isSearchable={false}
                                                onChange={(selected) => field.onChange(selected?.value || "")}
                                            />
                                        )}
                                    />
                                    <ErrorMessage message={errors.country?.message} />
                                </div>
                                <div className="sm:col-span-2">
                                    <Controller
                                        name="location"
                                        control={control}
                                        rules={{ required: t("AUTH_VALIDATION_LOCATION_REQUIRED") }}
                                        render={({ field }) => (
                                            <LocationAutocomplete
                                                value={field.value}
                                                onChange={field.onChange}
                                                placeholder={t("AUTH_PLACEHOLDER_LOCATION")}
                                            />
                                        )}
                                    />
                                    <ErrorMessage message={errors.location?.message} />
                                </div>
                                <div className="sm:col-span-2">
                                    <Controller
                                        name="phone"
                                        control={control}
                                        rules={{
                                            required: t("AUTH_VALIDATION_PHONE_REQUIRED"),
                                            pattern: {
                                                value: /^\+?[\d\s\-().]{7,20}$/,
                                                message: t("AUTH_VALIDATION_PHONE_INVALID"),
                                            },
                                        }}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                onChange={(e) => field.onChange(e.target.value.replace(/[^\d+\s\-().]/g, ""))}
                                                type="tel"
                                                placeholder={t("AUTH_PLACEHOLDER_PHONE")}
                                                className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-700 outline-none focus:outline-none transition focus:border-primary"
                                            />
                                        )}
                                    />
                                    <ErrorMessage message={errors.phone?.message} />
                                </div>
                                <div className="sm:col-span-2 grid grid-cols-2 gap-4">
                                    <div>
                                        <Controller
                                            name="age"
                                            control={control}
                                            rules={{ required: t("AUTH_VALIDATION_AGE_REQUIRED") }}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    options={ageOptions}
                                                    placeholder={t("AUTH_PLACEHOLDER_AGE")}
                                                    classNamePrefix="react-select"
                                                    components={{ DropdownIndicator }}
                                                    styles={registerSelectStyles}
                                                />
                                            )}
                                        />
                                        <ErrorMessage message={errors.age?.message} />
                                    </div>
                                    <div>
                                        <Controller
                                            name="categories"
                                            control={control}
                                            rules={{ validate: (v) => (v && v.length > 0) || t("AUTH_VALIDATION_CATEGORY_REQUIRED") }}
                                            render={({ field }) => {
                                                const hasAll = (field.value || []).some((v) => v.value === "all");
                                                return (
                                                    <Select
                                                        {...field}
                                                        isMulti
                                                        options={[
                                                            { value: "all", label: t("COMMON_ALL") },
                                                            ...categoryOptions,
                                                        ]}
                                                        placeholder={t("AUTH_PLACEHOLDER_CATEGORY")}
                                                        classNamePrefix="react-select"
                                                        components={{ DropdownIndicator, Option: CheckboxOption }}
                                                        styles={{
                                                            ...registerSelectStyles,
                                                            multiValue: (base) => ({
                                                                ...base,
                                                                backgroundColor: "#e8eafc",
                                                                borderRadius: "999px",
                                                                padding: "0 2px",
                                                                flexShrink: 0,
                                                                overflow: "visible",
                                                            }),
                                                            multiValueLabel: (base) => ({
                                                                ...base,
                                                                color: "#0f1b8f",
                                                                fontSize: "11px",
                                                                padding: "1px 4px",
                                                                whiteSpace: "nowrap",
                                                                overflow: "visible",
                                                                textOverflow: "unset",
                                                            }),
                                                            multiValueRemove: (base) => ({
                                                                ...base,
                                                                color: "#0f1b8f",
                                                                borderRadius: "999px",
                                                                ":hover": { backgroundColor: "#d0d4f8", color: "#0f1b8f" },
                                                            }),
                                                            valueContainer: (base) => ({
                                                                ...base,
                                                                flexWrap: "nowrap",
                                                                overflowX: "auto",
                                                                scrollbarWidth: "none",
                                                                msOverflowStyle: "none",
                                                                padding: "0 8px",
                                                            }),
                                                            option: (base, state) => ({
                                                                ...base,
                                                                backgroundColor: state.isFocused && !state.isDisabled ? "#f1f5f9" : "transparent",
                                                                color: state.isDisabled ? "#94a3b8" : "#334155",
                                                                cursor: state.isDisabled ? "not-allowed" : "pointer",
                                                            }),
                                                        }}
                                                        hideSelectedOptions={false}
                                                        closeMenuOnSelect={false}
                                                        isOptionDisabled={(option) => option.value !== "all" && hasAll}
                                                        onChange={(selected) => {
                                                            const selectedAll = selected.some((s) => s.value === "all");
                                                            if (selectedAll && !hasAll) {
                                                                field.onChange([{ value: "all", label: t("COMMON_ALL") }]);
                                                            } else {
                                                                field.onChange(selected.filter((s) => s.value !== "all"));
                                                            }
                                                        }}
                                                    />
                                                );
                                            }}
                                        />
                                        <ErrorMessage message={errors.categories?.message} />
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSocialError("");
                                        setShowSocialModal(true);
                                    }}
                                    className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-sm transition sm:col-span-2 ${
                                        isSocialAdded
                                            ? "border-primary bg-white text-primary focus:outline-none"
                                            : "border-slate-200 bg-input-bg text-slate-500 hover:border-slate-300 focus:outline-none"
                                    }`}
                                >
                                    <span>
                                        {isSocialAdded ? t("AUTH_REGISTER_SOCIAL_ADDED") : t("AUTH_REGISTER_SOCIAL_CONNECT")}
                                    </span>
                                    <span
                                        className={`flex h-6 w-6 items-center justify-center rounded-full border text-slate-500 ${
                                            isSocialAdded ? "border-primary text-primary" : "border-slate-300"
                                        }`}
                                    >
                                        {isSocialAdded ? (
                                            <ArrowRight className="h-3.5 w-3.5" />
                                        ) : (
                                            <Plus className="h-3.5 w-3.5" />
                                        )}
                                    </span>
                                </button>
                                {socialError ? (
                                    <div className="sm:col-span-2">
                                        <ErrorMessage message={socialError} />
                                    </div>
                                ) : null}
                                {!socialError && isSocialAdded && hasUnverifiedSocial ? (
                                    <div className="sm:col-span-2">
                                        <ErrorMessage message={t("AUTH_REGISTER_SOCIAL_UNVERIFIED_HINT", "Please verify all added social accounts before submitting.")} />
                                    </div>
                                ) : null}
                                <div className="sm:col-span-2">
                                    <div className="max-w-lg">
                                        <Controller
                                            name="acceptedAge"
                                            control={control}
                                            rules={{ required: t("AUTH_VALIDATION_ACCEPT_AGE_REQUIRED") }}
                                            render={({ field }) => (
                                                <CustomCheckbox
                                                    name={field.name}
                                                    checked={field.value}
                                                    onChange={field.onChange}
                                                    label={t("AUTH_REGISTER_ACCEPT_AGE")}
                                                    required
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="sm:col-span-2">
                                    <div className="mt-2 max-w-lg">
                                        <Controller
                                            name="acceptedTerms"
                                            control={control}
                                            rules={{ required: t("AUTH_VALIDATION_ACCEPT_TERMS_REQUIRED") }}
                                            render={({ field }) => (
                                                <CustomCheckbox
                                                    name={field.name}
                                                    checked={field.value}
                                                    onChange={field.onChange}
                                                    label={(
                                                        <span>
                                                            {t("AUTH_REGISTER_ACCEPT_TERMS_PREFIX")}{" "}
                                                            <a
                                                                href="/terms-conditions"
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="underline underline-offset-4 text-primary"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {t("COMMON_TERMS_AND_CONDITIONS")}
                                                            </a>
                                                        </span>
                                                    )}
                                                    required
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="sm:col-span-2">
                                    <div className="max-w-lg">
                                        <Controller
                                            name="acceptedMarketing"
                                            control={control}
                                            rules={{ required: t("AUTH_VALIDATION_ACCEPT_MARKETING_REQUIRED") }}
                                            render={({ field }) => (
                                                <CustomCheckbox
                                                    name={field.name}
                                                    checked={field.value}
                                                    onChange={field.onChange}
                                                    label={(
                                                        <span>
                                                            {t("AUTH_REGISTER_MARKETING_PREFIX")}{" "}
                                                            <a
                                                                href="/privacy-policy"
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="underline underline-offset-4 text-primary"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {t("COMMON_PRIVACY_POLICY")}
                                                            </a>
                                                        </span>
                                                    )}
                                                    required
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="sm:col-span-2">
                                    <div className="max-w-lg">
                                        <Controller
                                            name="acceptedSms"
                                            control={control}
                                            render={({ field }) => (
                                                <CustomCheckbox
                                                    name={field.name}
                                                    checked={field.value}
                                                    onChange={field.onChange}
                                                    label={t("AUTH_REGISTER_ACCEPT_SMS")}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="sm:col-span-2">
                                    <Turnstile
                                        siteKey={env.REACT_APP_TURNSTILE_SITE_KEY}
                                        onSuccess={async (token) => {
                                            setTurnstileToken(token);
                                            const verified = await verifyTurnstile(token);
                                            setTurnstileVerified(verified);
                                        }}
                                        onExpire={() => { setTurnstileToken(null); setTurnstileVerified(false); }}
                                        onError={() => { setTurnstileToken(null); setTurnstileVerified(false); }}
                                        options={{ theme: "light", size: "flexible" }}
                                    />
                                </div>
                                {!isRegistrationEnabled ? (
                                    <div className="sm:col-span-2 text-sm text-center leading-relaxed" style={{ color: "#E8323E" }}>
                                        <p>{t("AUTH_REGISTER_CLOSED_LINE1")}</p>
                                        <p>{t("AUTH_REGISTER_CLOSED_LINE2")}</p>
                                    </div>
                                ) : null}
                                {submitError ? (
                                    <div className="sm:col-span-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm leading-relaxed text-red-700">
                                        {submitError}
                                    </div>
                                ) : null}
                                <Button
                                    type="submit"
                                    disabled={!canSubmit}
                                    className="mt-2 w-full text-center sm:col-span-2"
                                >
                                    {isLoading ? t("COMMON_SUBMITTING") : t("AUTH_REGISTER_SUBMIT_APPLICATION")}
                                </Button>
                            </form>
                            <div className="mt-6 text-xs text-slate-500 text-center max-w-lg">
                                {t("AUTH_REGISTER_ALREADY_HAVE_ACCOUNT")}{" "}
                                <Link to="/login" className="font-semibold text-primary underline-offset-4 hover:underline">
                                    {t("AUTH_LOGIN_SIGN_IN")}
                                </Link>
                            </div>
                        </div>
                    </div>
                }
            />

            <SocialModal
                isOpen={showSocialModal}
                onClose={() => setShowSocialModal(false)}
                platforms={platforms}
                handles={handles}
                links={socialLinks}
                onHandleChange={handleChange}
                onLinkChange={handleLinkChange}
                onRemoveEntry={handleRemoveEntry}
                showVerification={true}
                allowEditExisting={true}
                verifiedPlatforms={verifiedPlatforms}
                onBeforeVerify={handleBeforeVerify}
                errorMessage={socialError}
            />

            <LearnMoreModal
                isOpen={showLearnModal}
                onClose={() => setShowLearnModal(false)}
            />
        </>
    );
};

export default Register;
